// websocket/owed-commit.ts — Janus Session A (2026-09-30, Codex step-11 review P1): a move's CONSEQUENCE (a real fall, a trigger tail) is
// OWED once the landing is saved — its dice are already drawn and shown. A transport failure must not drop it: retry with backoff under the
// same derived operation id (the commit is idempotent: a saved attempt answers from its receipt), and only after that surface the loss
// loudly. The caller's movement hold (movement-truth.ts queueAfterAccepted) stays on the actor until this settles.
type Outcome = { outcome: 'served' | 'refused' | 'stale' | 'error'; parked?: boolean };
const BACKOFF_MS = [0, 3000, 8000, 20000, 45000];

export async function commitOwed<T extends Outcome>(label: string, send: () => Promise<T>, detail: Record<string, unknown>): Promise<T | { outcome: 'error' }> {
  let result: T | { outcome: 'error'; parked?: boolean } = { outcome: 'error' };
  for (const delay of BACKOFF_MS) {
    if (delay) await new Promise(resolve => setTimeout(resolve, delay));
    try { result = await send(); } catch { result = { outcome: 'error' }; }
    // a PARKED commit (the outbox already retried it) must not be re-sent behind itself — the delivery notice keeps its Retry
    if (result.outcome !== 'error' || result.parked) break;
  }
  if (result.outcome === 'served') return result;
  // every other terminal answer leaves an owed consequence unsaved: say so, loudly (Codex step-12 review P2)
  window.dispatchEvent(new CustomEvent('ft:owed-consequence-lost', { detail: { label, outcome: result.outcome, ...detail } }));
  const warn = (window as unknown as { displayWarning?: (message: string) => void }).displayWarning;
  const why = result.outcome === 'error' ? 'the connection failed' : result.outcome === 'stale' ? 'the creature changed meanwhile' : 'the server refused it';
  if (typeof warn === 'function') warn(`${label} could not be saved (${why}). The move stands; ask the GM to apply it by hand.`);
  return result;
}
